import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import type { FieldErrors, FieldValues, Path, UseFormRegister } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Barcode, FileText, Package } from 'lucide-react';
import {
  addHardwareItem, createStockItem, deleteStockItem, getStockItemAccessories, getStockItemDevices, listStockItems,
  putStockItemAccessories, putStockItemDevices,
  getStockItemComponents, putStockItemComponents,
  recordStockTransaction, updateStockItem,
} from '@/api/stock';
import { getHardwareCatalogue } from '@/api/hardwareCatalogue';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { ApiError } from '@/api/client';
import { useSort, sortRows, matchesQuery } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import { DesktopTable, MobileCards, MobileCard, CardField } from '@/components/common/responsiveList';
import { getLocations } from '@/api/locations';
import { HardwareCatalogue } from '@/pages/HardwareCatalogue';
import { HistoryDialog } from './HistoryDialog';
import { ComponentsEditor } from './ComponentsEditor';
import { QuoteRequestDialog, QuoteRequestPrint } from './QuoteRequest';
import type { QuoteLine } from './QuoteRequest';
import { sameAsSaved, toDrafts, toWrite } from './componentDrafts';
import type { PartDraft } from './componentDrafts';
import type { AssetCategory, StockItem, StockItemType, StockItemWrite, StockTxnType } from '@/types/oscar';

/**
 * ⚠ Must stay in step with the `Categories` array in StockItemsFunctions.cs,
 * which rejects anything else, and with AssetCategory.
 */
const STOCK_CATEGORIES = [
  'Laptop', 'Desktop', 'Tablet', 'Mobile', 'MFD', 'Server', 'Network', 'SIM', 'Other',
] as const satisfies readonly AssetCategory[];

const itemSchema = z.object({
  Name: z.string().trim().min(1, 'Name is required'),
  ItemType: z.enum(['Accessory', 'Consumable', 'Device']),
  IsSerialised: z.boolean().optional(),
  // ⚠ A defined set, not free text, and the SAME set as oscar.Asset.Category —
  // a serialised line becomes an asset when it is issued, so it should already
  // carry the category that asset will have.
  // ⚠ '' IS THE UNSELECTED STATE and must be allowed. A bare z.enum rejects it,
  // and the form then failed validation with no message anywhere — the Add
  // button simply did nothing. Category is optional; the empty option means so.
  Category: z.enum(STOCK_CATEGORIES).or(z.literal('')).optional(),
  // ⛔ REQUIRED. oscar.StockItem carries ONE LocationId, so a count with no
  // warehouse is a count of something nobody can go and fetch. Three existing
  // rows are in exactly that state, which is why new ones must say.
  LocationId: z.number({ invalid_type_error: 'A warehouse is required' }).int().positive(),
  MinQty: z.number().int().nonnegative().nullable().optional(),
  UnitCost: z.number().nonnegative().nullable().optional(),
  InitialQty: z.number().int().nonnegative().optional(),

  // ── What a supplier is sent (099) ──────────────────────────────────────────
  // ⚠ The max lengths are the COLUMN widths migration 099 declared, restated
  // here so the message arrives before the round trip. The server checks them
  // too and is the real guard — Microsoft.Data.SqlClient truncates silently,
  // so a specification that only the client checked could reach a creditor with
  // its last sentence missing.
  Make: z.string().trim().max(100, 'Make cannot be longer than 100 characters').optional(),
  Model: z.string().trim().max(200, 'Model cannot be longer than 200 characters').optional(),
  SupplierPartNumber: z.string().trim()
    .max(100, 'Part number cannot be longer than 100 characters').optional(),
  Description: z.string().trim()
    .max(1000, 'Description cannot be longer than 1000 characters').optional(),
  Notes: z.string().optional(),
});
type ItemFormData = z.infer<typeof itemSchema>;

/**
 * Editing an existing line (099).
 *
 * ⛔ FOUR FIELDS ARE NOT HERE, AND EACH FOR ITS OWN REASON.
 *   • ItemType — PatchStockItem does not accept it (WritableColumns), because
 *     turning an Accessory into a Device changes what the ledger means.
 *   • IsSerialised — it changes how a hardware request ALLOCATES this line.
 *     Flipping it on a line that already holds stock would re-interpret
 *     movements that have already happened. Set at creation, deliberately.
 *   • LocationId — a stock line is a quantity AT A PLACE, so changing it moves
 *     stock between warehouses with no ledger entry to say so. That needs to be
 *     a movement, not an edit.
 *   • InitialQty — quantity only ever moves through oscar.StockTransaction.
 */
const editSchema = itemSchema.omit({
  ItemType: true, IsSerialised: true, LocationId: true, InitialQty: true,
});
type EditFormData = z.infer<typeof editSchema>;

/**
 * ⛔ Number(null) IS 0, NOT NaN, AND THAT IS A DATA BUG. A number box nobody
 * touched still holds its default — null for "no threshold", null for "no
 * recorded cost" — and the bare `v === '' ? null : Number(v)` this file used
 * turned that null into a deliberate-looking ZERO on the way out.
 *
 * <p>⚠ A stock line saved with a threshold of 0 can never read Low: the check
 * is QtyOnHand &lt; MinQty, so an empty shelf compares 0 &lt; 0 and says it is
 * fine. Nothing on any screen would say why, which is what makes it worth a
 * named helper rather than a longer ternary in four places.</p>
 */
const numberOrNull = (v: unknown): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const inputCls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal';

/**
 * One labelled box in the quote fieldset.
 *
 * ⛔ htmlFor/id, NOT A WRAPPING LABEL. Every box here carries a hint, and a
 * hint nested inside the label becomes part of the control's accessible NAME —
 * "DescriptionGoes to the supplier…". A screen reader announces the lot, and a
 * test looking for the field by its label stops finding it, which is exactly
 * how this was caught.
 *
 * ⛔ AND DECLARED AT MODULE SCOPE, WHICH IS NOT STYLE. A component defined
 * inside SpecFields would be a NEW TYPE on every render, so React would unmount
 * and remount these inputs — and an uncontrolled input that remounts loses what
 * was typed into it.
 *
 * ⚠ The ids are fixed rather than generated because Add and Edit are never open
 * at the same time. Two instances on one screen would collide, which is the
 * defect the hardware-request PersonPicker had to be given idSuffix for.
 */
function SpecField({ name, label, hint, error, children }: {
  name: string; label: string; hint?: string; error?: string; children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={`spec-${name}`} className="block text-xs font-semibold text-darkGrey">
        {label}
      </label>
      {children}
      {hint && <span className="mt-0.5 block text-xs font-normal text-darkGrey/70">{hint}</span>}
      {error && <span className="block text-xs font-normal text-red">{error}</span>}
    </div>
  );
}

/**
 * What a supplier is told this is (099).
 *
 * <p>Anthony, 08/10/2026: <i>"for stock - we need a description area in stock so
 * we can do a request for quote when we need to procure stock."</i></p>
 *
 * ⛔ THE WHOLE POINT IS THE SEPARATION FROM Notes. A stock line is named for
 * the warehouse — "Laptop (High Spec)" — and no supplier can price a tier. Two
 * suppliers asked to quote that will quote two different machines. Notes has
 * existed since migration 004 and is internal: it may say "these keep failing"
 * or name a person. Writing the specification there would mail our own remarks
 * to a creditor, so they are two boxes with two labels that say which is which.
 *
 * ⚠ Shared by Add and Edit so the two cannot drift. The generic plumbing is
 * the price of that: both forms carry these fields, but they are different
 * schemas, so the field names have to be cast to each form's own Path.
 */
function SpecFields<T extends FieldValues>({ register, errors }: {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
}) {
  const field = (name: string) => register(name as Path<T>);
  const message = (name: string) =>
    (errors as Record<string, { message?: string } | undefined>)[name]?.message;

  return (
    <fieldset className="rounded border border-border bg-lightBlue/20 px-2 py-2">
      <legend className="px-1 text-xs font-semibold text-darkGrey">For a request for quote</legend>
      <p className="text-xs font-normal text-darkGrey/70">
        The name above is what the warehouse reads. This is what a supplier is sent when we
        ask them to price it.
      </p>

      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <SpecField error={message('Make')} name="Make" label="Make">
          <input id="spec-Make" {...field('Make')} className={inputCls} placeholder="Dell" />
        </SpecField>
        <SpecField error={message('Model')} name="Model" label="Model">
          <input id="spec-Model" {...field('Model')} className={inputCls} placeholder="Latitude 5550" />
        </SpecField>
      </div>

      {/* ⛔ THE FIELD THAT MAKES TWO QUOTES COMPARABLE. Without it a supplier
          substitutes whatever they have and the quote still looks like an
          answer — and nothing can check the delivery against the order. */}
      <div className="mt-2">
        <SpecField
          error={message('SupplierPartNumber')}
          name="SupplierPartNumber"
          label="Supplier part number"
          hint="The manufacturer’s part number, so every supplier quotes the same thing."
        >
          <input
            id="spec-SupplierPartNumber"
            {...field('SupplierPartNumber')}
            className={inputCls}
            placeholder="MPN or SKU"
          />
        </SpecField>
      </div>

      <div className="mt-2">
        <SpecField
          error={message('Description')}
          name="Description"
          label="Description"
          hint="Goes to the supplier — write it for somebody outside Onsite. Up to 1000 characters."
        >
          <textarea
            id="spec-Description"
            {...field('Description')}
            rows={4}
            className={inputCls}
            placeholder="Processor, memory, storage, warranty term, what is in the box."
          />
        </SpecField>
      </div>

      {/* ⚠ WHAT THIS WAREHOUSE PAID, not what a catalogue device is budgeted
          at. 083 put the budget figure on the catalogue option for exactly this
          reason; this one is a reference for the next quote. */}
      <div className="mt-2">
        <SpecField
          error={message('UnitCost')}
          name="UnitCost"
          label="Indicative unit cost (ex GST)"
          hint="What we last paid, as a reference when a quote comes back. Leave blank to keep the current value."
        >
          <input
            id="spec-UnitCost"
            type="number"
            step="0.01"
            {...register('UnitCost' as Path<T>, { setValueAs: numberOrNull })}
            className={inputCls}
          />
        </SpecField>
      </div>

      {/* ⛔ INTERNAL, AND LABELLED SO. The separation from Description is the
          reason 099 added a second column rather than reusing this one. */}
      <div className="mt-2">
        <SpecField error={message('Notes')} name="Notes" label="Internal notes" hint="Stays in Oscar. Not sent to a supplier.">
          <textarea id="spec-Notes" {...field('Notes')} rows={2} className={inputCls} />
        </SpecField>
      </div>
    </fieldset>
  );
}

/**
 * Stock — the shelf, and what people can ask for, on one page.
 *
 * ⛔ ONE PLACE (Anthony, 01/11/2026: "I thought we were combining this into stock"). The Hardware
 * catalogue used to be a second screen you also had to visit. It is now the second view here:
 * `?view=catalogue`. The old /hardware-catalogue address redirects to it, so bookmarks and the
 * menu entry still land in the right place.
 *
 * ⚠ THE VIEW LIVES IN THE URL, never localStorage, so a link opens the same view and Back works.
 */
export function StockPage() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'catalogue' ? 'catalogue' : 'shelf';
  const show = (v: 'shelf' | 'catalogue') => setParams(v === 'shelf' ? {} : { view: v }, { replace: true });

  const tab = (v: 'shelf' | 'catalogue', label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={view === v}
      onClick={() => show(v)}
      className={`border-b-2 px-4 py-2 text-sm font-semibold ${
        view === v ? 'border-orange text-navy' : 'border-transparent text-darkGrey/70 hover:text-navy'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div>
      {/* print:hidden - the quote print view must not carry the tab bar onto the paper. */}
      <div role="tablist" aria-label="Stock views" className="mb-4 flex gap-1 border-b border-slate-200 print:hidden">
        {tab('shelf', 'On the shelf')}
        {tab('catalogue', 'What people can ask for')}
      </div>
      {view === 'catalogue' ? <HardwareCatalogue embedded /> : <StockShelf onShowCatalogue={() => show('catalogue')} />}
    </div>
  );
}

function StockShelf({ onShowCatalogue }: { onShowCatalogue: () => void }) {
  const { canMutate, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [itemType, setItemType] = useState('');
  const [category, setCategory] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [search, setSearch] = useState('');
  const { sortKey, sortDir, onSort } = useSort('name');
  const [adding, setAdding] = useState(false);
  const [txnFor, setTxnFor] = useState<StockItem | null>(null);
  const [devicesFor, setDevicesFor] = useState<StockItem | null>(null);
  const [accessoriesFor, setAccessoriesFor] = useState<StockItem | null>(null);
  const [editFor, setEditFor] = useState<StockItem | null>(null);

  // ── choosing lines to get quoted (13/10/2026) ─────────────────────────────
  // ⚠ Ids, not rows. A filter change must not silently drop a tick, and a row
  //   object captured here would go stale the moment the list refetched.
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [quoting, setQuoting] = useState(false);
  const [printing, setPrinting] = useState<
    { lines: QuoteLine[]; supplier: string; reference: string } | null>(null);
  const [retireFor, setRetireFor] = useState<StockItem | null>(null);
  const [historyFor, setHistoryFor] = useState<StockItem | null>(null);

  const query = useQuery({
    queryKey: ['stock-items', { itemType, lowStockOnly }],
    queryFn: ({ signal }) => listStockItems({ itemType, lowStockOnly }, signal),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['stock-items'] });

  const colValue = (s: StockItem, k: string): unknown =>
    ({ name: s.Name, type: s.ItemType, category: s.Category, qty: s.QtyOnHand } as Record<string, unknown>)[k];
  const categoryOpts = [...new Set((query.data ?? []).map((s) => s.Category).filter((c): c is string => !!c))].sort();
  const rows = sortRows(
    (query.data ?? []).filter((s) => matchesQuery(search, [s.Name, s.Category, s.Make, s.Model]) && (!category || s.Category === category)),
    (s) => colValue(s, sortKey),
    sortDir,
  );

  const toggle = (id: number) => setPicked((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  // ⚠ The VISIBLE rows, not every row. "Select all" under a filter that is
  //   showing eight of ninety must mean those eight.
  const allShownPicked = rows.length > 0 && rows.every((r) => picked.has(r.StockItemId));
  const toggleAllShown = () => setPicked((prev) => {
    const next = new Set(prev);
    if (allShownPicked) rows.forEach((r) => next.delete(r.StockItemId));
    else rows.forEach((r) => next.add(r.StockItemId));
    return next;
  });

  // ⚠ Resolved from the CURRENT list, so a tick on a row that has since been
  //   retired simply drops out rather than printing a stale copy of it.
  const pickedItems = (query.data ?? []).filter((r) => picked.has(r.StockItemId));

  // ⛔ THE PRINT VIEW REPLACES THE PAGE rather than overlaying it. A browser
  //    prints the whole document, so a list still mounted behind a dialog would
  //    print too - pages of stock rows ahead of the quote request.
  if (printing) {
    return (
      <QuoteRequestPrint
        lines={printing.lines}
        supplier={printing.supplier}
        reference={printing.reference}
        onBack={() => setPrinting(null)}
      />
    );
  }

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Stock</h1>
          {/* ⛔ WHAT THIS SCREEN IS, next to its twin. The catalogue is what people can
              ASK for; this is what is on the SHELF. A device line answers the devices
              it can fill, and that is set from the catalogue side. */}
          <p className="mt-1 text-sm text-darkGrey/80">
            What we hold, by warehouse: devices, accessories and consumables, counted by quantity.
            A device line <strong>answers</strong> the devices people can ask for &mdash; see and change that
            under a device&rsquo;s <em>Details</em> in{' '}
            <button type="button" onClick={onShowCatalogue} className="font-semibold text-navy hover:underline">What people can ask for</button>.
          </p>
        </div>
        {canMutate && (
          <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
            <Package className="h-4 w-4" aria-hidden /> Add item
          </button>
        )}
      </header>

      {picked.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded border border-navy/30 bg-lightBlue/30 px-3 py-2">
          <p className="text-sm font-semibold text-navy">
            {picked.size} item{picked.size === 1 ? '' : 's'} selected
          </p>
          <button
            type="button"
            onClick={() => setQuoting(true)}
            className="inline-flex items-center gap-2 rounded bg-navy px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
          >
            <FileText className="h-4 w-4" aria-hidden /> Request a quote
          </button>
          <button
            type="button"
            onClick={() => setPicked(new Set())}
            className="text-xs font-semibold text-navy hover:underline"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Type
          <select aria-label="Filter by item type" value={itemType} onChange={(e) => setItemType(e.target.value)} className={inputCls}>
            <option value="">All</option>
            <option value="Accessory">Accessory</option>
            <option value="Consumable">Consumable</option>
            <option value="Device">Device</option>
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Category
          <select aria-label="Filter by category" value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">All</option>
            {categoryOpts.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 pb-1.5 text-xs font-semibold text-darkGrey">
          <input type="checkbox" checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} /> Low stock only
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input aria-label="Search stock" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, category, make or model" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
      </div>

      <DesktopTable className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr>
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all shown"
                  checked={allShownPicked}
                  onChange={toggleAllShown}
                />
              </th>
              <SortHeader label="Name" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Type" columnKey="type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="Category" columnKey="category" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortHeader label="On hand" columnKey="qty" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {query.isLoading && <tr><td colSpan={5} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>}
            {query.isError && <tr><td colSpan={5} className="px-3 py-6 text-center text-red">Could not load stock.</td></tr>}
            {rows.length === 0 && !query.isLoading && <tr><td colSpan={5} className="px-3 py-6 text-center text-darkGrey/60">No stock items.</td></tr>}
            {rows.map((s) => {
              // ⛔ THE SERVER'S ANSWER, not a re-derivation (080). A device
              // line is low on the COMPANY TOTAL for a device it answers; an
              // accessory is low on its own MinQty. Recomputing either here
              // would make this list disagree with the detail page.
              const low = s.IsLow;
              return (
                <tr key={s.StockItemId} className="border-t border-border hover:bg-bg/60">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${s.Name}`}
                      checked={picked.has(s.StockItemId)}
                      onChange={() => toggle(s.StockItemId)}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-navy">{s.Name}</td>
                  <td className="px-3 py-2 text-darkGrey/80">
                    {s.ItemType}
                    {/* ⛔ The one thing about a stock line that changes how a
                        hardware request behaves. Said on the row rather than
                        only in the editor, because "why did that allocate one
                        at a time?" is asked from the list. */}
                    {s.IsSerialised && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-navy/10 px-2 py-0.5 text-xs font-semibold text-navy">
                        <Barcode className="h-3 w-3" aria-hidden /> By serial
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-darkGrey/80">{s.Category ?? '—'}</td>
                  <td className="px-3 py-2">
                    {s.QtyOnHand}
                    {low && <span className="ml-2 inline-flex rounded-full bg-red/10 px-2 py-0.5 text-xs font-semibold text-red">Low</span>}
                    <LowReason item={s} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canMutate && <button type="button" onClick={() => setTxnFor(s)} className="text-xs font-semibold text-navy hover:underline">Movement…</button>}
                    <button
                      type="button"
                      aria-label={`History for ${s.Name}`}
                      onClick={() => setHistoryFor(s)}
                      className={`text-xs font-semibold text-navy hover:underline${canMutate ? ' ml-3' : ''}`}
                    >
                      History…
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        aria-label={`Requests ${s.Name} can satisfy`}
                        onClick={() => setDevicesFor(s)}
                        className="ml-3 text-xs font-semibold text-navy hover:underline"
                      >
                        Satisfies…
                      </button>
                    )}
                    {/* ⚠ ACCESSORY LINES ONLY (oscar migration 124): which accessory
                        on a request this line can answer. A device line answers
                        devices and a consumable is used up. */}
                    {isAdmin && s.ItemType === 'Accessory' && (
                      <button
                        type="button"
                        aria-label={`Accessories ${s.Name} can satisfy`}
                        onClick={() => setAccessoriesFor(s)}
                        className="ml-3 text-xs font-semibold text-navy hover:underline"
                      >
                        Accessories…
                      </button>
                    )}
                    {canMutate && (
                      <button
                        type="button"
                        aria-label={`Edit ${s.Name}`}
                        onClick={() => setEditFor(s)}
                        className="ml-3 text-xs font-semibold text-navy hover:underline"
                      >
                        Edit…
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        type="button"
                        aria-label={`Retire ${s.Name}`}
                        onClick={() => setRetireFor(s)}
                        className="ml-3 text-xs font-semibold text-red hover:underline"
                      >
                        Retire…
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DesktopTable>

      {/* Mobile (<md): stock items as stacked cards. */}
      <MobileCards>
        {query.isLoading && <p className="px-1 py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
        {query.isError && <p className="px-1 py-6 text-center text-sm text-red">Could not load stock.</p>}
        {rows.length === 0 && !query.isLoading && (
          <p className="px-1 py-6 text-center text-sm text-darkGrey/60">No stock items.</p>
        )}
        {rows.map((s) => {
          const low = s.IsLow;
          return (
            <MobileCard key={s.StockItemId}>
              <div className="flex items-start justify-between gap-2">
                <label className="flex min-w-0 items-start gap-2">
                  <input
                    type="checkbox"
                    aria-label={`Select ${s.Name}`}
                    checked={picked.has(s.StockItemId)}
                    onChange={() => toggle(s.StockItemId)}
                    className="mt-0.5 shrink-0"
                  />
                  <span className="text-sm font-semibold text-navy">{s.Name}</span>
                </label>
                {low && <span className="inline-flex shrink-0 rounded-full bg-red/10 px-2 py-0.5 text-xs font-semibold text-red">Low</span>}
              </div>
              <div className="mt-2 border-t border-border pt-2">
                <CardField label="Type">
                  {s.ItemType}{s.IsSerialised ? ' · by serial' : ''}
                </CardField>
                <CardField label="Category">{s.Category ?? '—'}</CardField>
                <CardField label="On hand">
                  {s.QtyOnHand}
                  <LowReason item={s} />
                </CardField>
                <CardField label="Answers">
                  {s.Devices.length > 0
                    ? s.Devices.map((d) => d.DeviceName).join(', ')
                    : <span className="text-darkGrey/60">nothing yet</span>}
                </CardField>
              </div>
              {canMutate && (
                <div className="mt-2 text-right">
                  <button type="button" onClick={() => setTxnFor(s)} className="text-xs font-semibold text-navy hover:underline">Movement…</button>
                  <button
                    type="button"
                    aria-label={`History for ${s.Name}`}
                    onClick={() => setHistoryFor(s)}
                    className="ml-3 text-xs font-semibold text-navy hover:underline"
                  >
                    History…
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      aria-label={`Requests ${s.Name} can satisfy`}
                      onClick={() => setDevicesFor(s)}
                      className="ml-3 text-xs font-semibold text-navy hover:underline"
                    >
                      Satisfies…
                    </button>
                  )}
                  {isAdmin && s.ItemType === 'Accessory' && (
                    <button
                      type="button"
                      aria-label={`Accessories ${s.Name} can satisfy`}
                      onClick={() => setAccessoriesFor(s)}
                      className="ml-3 text-xs font-semibold text-navy hover:underline"
                    >
                      Accessories…
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label={`Edit ${s.Name}`}
                    onClick={() => setEditFor(s)}
                    className="ml-3 text-xs font-semibold text-navy hover:underline"
                  >
                    Edit…
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      aria-label={`Retire ${s.Name}`}
                      onClick={() => setRetireFor(s)}
                      className="ml-3 text-xs font-semibold text-red hover:underline"
                    >
                      Retire…
                    </button>
                  )}
                </div>
              )}
            </MobileCard>
          );
        })}
      </MobileCards>

      {adding && <AddItemDialog onClose={() => setAdding(false)} onSaved={() => { invalidate(); setAdding(false); }} />}
      {txnFor && <TransactionDialog item={txnFor} onClose={() => setTxnFor(null)} onSaved={() => { invalidate(); setTxnFor(null); }} />}
      {devicesFor && <DevicesDialog item={devicesFor} onClose={() => setDevicesFor(null)} />}
      {accessoriesFor && <AccessoriesDialog item={accessoriesFor} onClose={() => setAccessoriesFor(null)} />}
      {quoting && (
        <QuoteRequestDialog
          items={pickedItems}
          onClose={() => setQuoting(false)}
          onReady={(lines, supplier, reference) => {
            setQuoting(false);
            setPrinting({ lines, supplier, reference });
          }}
        />
      )}
      {editFor && <EditItemDialog item={editFor} onClose={() => setEditFor(null)} onSaved={() => { invalidate(); setEditFor(null); }} />}
      {retireFor && <RetireDialog item={retireFor} onClose={() => setRetireFor(null)} onSaved={() => { invalidate(); setRetireFor(null); }} />}
      {historyFor && <HistoryDialog item={historyFor} onClose={() => setHistoryFor(null)} />}
    </section>
  );
}

/**
 * ⛔ WHY THE NAME IS FREE TEXT AND NOT A CATALOGUE DROP-DOWN. The warehouse
 * counts SPEC TIERS — "High spec laptop", "Standard laptop", "OLT" — and all
 * three answer the one catalogue option a requester can ask for, `Laptop`.
 * Naming this line from the catalogue would collapse the three into one and
 * throw away the only thing the count is for.
 *
 * ⇒ So the catalogue appears here as the ticks instead: the name says what it
 *   IS, the ticks say what it ANSWERS.
 *
 * ⚠ Asked at CREATE time deliberately. A device line mapped to nothing is
 * offered against no request at all, and that silence looks exactly like a
 * stock problem. Before this it was a second trip through "Satisfies…" that
 * nobody had a reason to remember.
 */
/**
 * Why this line reads Low, when the reason is not on the line (080).
 *
 * ⛔ A DEVICE LINE IS LOW ON A NUMBER THAT IS NOT IN FRONT OF YOU — the company
 * total across every warehouse, against a threshold set on the catalogue
 * option. Without saying so, "Low" on a line showing 30 on hand looks like a
 * bug.
 *
 * ⚠ Silent for an accessory: there the threshold IS on the line, so the badge
 * needs no explaining.
 */
function LowReason({ item }: { item: StockItem }) {
  const low = item.Devices.filter((d) => d.IsLow);
  if (low.length === 0) return null;

  return (
    <span className="mt-0.5 block text-xs font-normal text-darkGrey/70">
      {low.map((d) => `${d.DeviceName}: ${d.TotalOnHand} of ${d.MinQty} company-wide`).join(' · ')}
    </span>
  );
}

function AddItemDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { isAdmin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [deviceIds, setDeviceIds] = useState<number[]>([]);
  // ⛔ ONE ADD, EVERYWHERE IT HAS TO EXIST (Marty, 29/10/2026). Off by default: ticked, the catalogue
  // option, the stock line and the mapping are created together in ONE call, so a failure leaves none
  // of them. Offered-for and required are an ACCESSORY's links; a device has none.
  const [inCatalogue, setInCatalogue] = useState(false);
  const [offeredIds, setOfferedIds] = useState<number[]>([]);
  const [requiredIds, setRequiredIds] = useState<number[]>([]);
  // ⚠ A line that is really several things can be described as it is created
  //   (106). Written in a SECOND call, because the parts need an id to hang off
  //   - the same two-step the device mapping below already uses.
  const [parts, setParts] = useState<PartDraft[]>([]);
  const { register, handleSubmit, formState, watch, getValues, setValue } = useForm<ItemFormData>({
    resolver: zodResolver(itemSchema),
    defaultValues: {
      Name: '', ItemType: 'Accessory', Category: '', LocationId: undefined,
      MinQty: null, UnitCost: null, InitialQty: 0, IsSerialised: false,
      Make: '', Model: '', SupplierPartNumber: '', Description: '', Notes: '',
    },
  });

  // ⚠ Admin only, mirroring "Satisfies…" on the list. PUT /devices is
  // oscar-admin; showing the ticks to helpdesk would let them configure a
  // mapping that 403s AFTER the item had already been created.
  const itemType = watch('ItemType');
  // ⛔ ADMIN ONLY, and only for what a requester can ask for: a consumable is consumed, not requested.
  const wantsCatalogue = isAdmin && inCatalogue && (itemType === 'Device' || itemType === 'Accessory');
  // ⚠ The old per-device ticks are for a stock line that answers EXISTING options. When the item is
  // being added to the catalogue too, it answers the option it is creating, so they are not used.
  const mapping = isAdmin && itemType === 'Device' && !wantsCatalogue;

  // ⚠ Stock locations only, not every branch. IsStockLocation is what makes a
  // place able to hold a count.
  const warehouses = useQuery({
    queryKey: ['locations', 'stock'],
    queryFn: ({ signal }) => getLocations({ isStockLocation: true, activeOnly: true }, signal),
  });

  // ⚠ The lines a PART can point at. Shares the list query's cache key, so
  //   opening this dialog does not refetch what the page already has.
  const stockLines = useQuery({
    queryKey: ['stock-items', { itemType: '', lowStockOnly: false }],
    queryFn: ({ signal }) => listStockItems({}, signal),
  });

  const catalogue = useQuery({
    queryKey: ['hardware-catalogue'],
    queryFn: ({ signal }) => getHardwareCatalogue(signal),
    enabled: mapping || (wantsCatalogue && itemType === 'Accessory'),
  });

  // ⚠ Retired options are left out here, unlike the edit dialog. That one has
  // to show a retired tick somebody already made; this one is choosing fresh,
  // and offering a retired device would only ever be a mistake.
  const options = (catalogue.data?.Devices ?? []).filter((d) => d.IsActive && !d.IsAccessoryOnly);

  // ⚠ For an ACCESSORY the "no device needed" option IS offered: that is how something like the Starlink
  // power adapter is asked for without a device. Retired options are still left out.
  const offerable = (catalogue.data?.Devices ?? []).filter((d) => d.IsActive);
  const toggleOffered = (id: number) => {
    setOfferedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    // ⚠ A flag on a device it is no longer offered for would be refused, so it goes with the device.
    setRequiredIds((prev) => prev.filter((x) => x !== id));
  };
  const toggleRequired = (id: number) =>
    setRequiredIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // ⛔ An accessory offered for nothing can never be picked - refused here, before anything is added.
  const offeredMissing = wantsCatalogue && itemType === 'Accessory' && offeredIds.length === 0;

  // ⛔ TWO WRITES, ONE BUTTON. If the mapping fails the item already exists, so
  // the id is kept and pressing Add again retries ONLY the mapping. Re-running
  // the create would leave a duplicate stock line behind every failed save.
  const createdId = useRef<number | null>(null);

  const mutation = useMutation({
    mutationFn: async (d: ItemFormData) => {
      if (createdId.current === null && wantsCatalogue) {
        const added = await addHardwareItem({
          Kind: itemType as 'Device' | 'Accessory',
          Name: d.Name.trim(),
          Category: d.Category || null,
          LocationId: d.LocationId as number,
          InitialQty: d.InitialQty ?? 0,
          MinQty: d.MinQty ?? null,
          UnitCost: d.UnitCost ?? null,
          IsSerialised: d.IsSerialised ?? false,
          Make: d.Make?.trim() || null,
          Model: d.Model?.trim() || null,
          SupplierPartNumber: d.SupplierPartNumber?.trim() || null,
          Description: d.Description?.trim() || null,
          Notes: d.Notes?.trim() || null,
          // ⚠ An accessory's links only. A device is offered for nothing.
          ...(itemType === 'Accessory'
            ? { DeviceOptionIds: offeredIds, RequiredDeviceOptionIds: requiredIds.filter((id) => offeredIds.includes(id)) }
            : {}),
        });
        createdId.current = added.StockItemId;
      }
      if (createdId.current === null) {
        const created = await createStockItem({
          Name: d.Name.trim(),
          ItemType: d.ItemType as StockItemType,
          Category: d.Category || null,
          LocationId: d.LocationId,
          MinQty: d.MinQty ?? null,
          UnitCost: d.UnitCost ?? null,
          InitialQty: d.InitialQty ?? 0,
          IsSerialised: d.IsSerialised ?? false,
          // ⚠ '' CLEARS AND undefined LEAVES ALONE on the server, and on a
          // CREATE the two are the same thing — the row has nothing yet.
          Make: d.Make?.trim() || null,
          Model: d.Model?.trim() || null,
          SupplierPartNumber: d.SupplierPartNumber?.trim() || null,
          Description: d.Description?.trim() || null,
          Notes: d.Notes?.trim() || null,
        });
        createdId.current = created.StockItemId;
      }
      if (mapping && deviceIds.length > 0) {
        await putStockItemDevices(createdId.current, deviceIds);
      }
      if (parts.length > 0) {
        await putStockItemComponents(createdId.current, toWrite(parts));
      }
      return createdId.current;
    },
    onSuccess: onSaved,
    onError: (err) => setError(
      createdId.current !== null
        ? wantsCatalogue
          // ⚠ The all-in-one call is ATOMIC, so reaching here means it succeeded and only the parts
          // (a second call, which needs the id) did not.
          ? 'The item was added to the stock and the catalogue, but what it is made of could not be '
            + 'saved. Close this and set it with “Edit…” on the list, or press Add to try again.'
          : 'The item was added, but what it satisfies or what it is made of could not be '
          + 'saved. Close this and set them with “Satisfies…” or “Edit…” on the list, '
          + 'or press Add to try again.'
        : err instanceof ApiError ? err.message : 'Could not add the item.',
    ),
  });

  /**
   * ⚠ ALIGNED BY DEFAULT, NOT BY FORCE. Anthony, 01/10/2026: the warehouse name
   * and what the requester reads should be the same once the catalogue carries
   * the spec tier itself — "Laptop – High spec" rather than a generic `Laptop`
   * answered by a warehouse tier nobody outside the warehouse can read.
   *
   * So ticking the first device fills an EMPTY name with it. It does not
   * overwrite a name already typed, and it does not rename on untick — a line
   * that legitimately differs (two tiers answering one option, a description
   * that carries a model) must stay possible.
   */
  const toggle = (id: number, deviceName: string) =>
    setDeviceIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (next.length === 1 && !prev.includes(id) && !getValues('Name').trim()) {
        setValue('Name', deviceName, { shouldValidate: true });
      }
      return next;
    });

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Add stock item"
      confirmLabel="Add"
      wide
      /* ⛔ THE DEFAULT IS TO CLOSE ON CONFIRM, WHICH THREW THE ERROR AWAY. This
         dialog has always rendered an error block; without this flag the dialog
         was gone before it could show one, so a failed save - and a failed
         validation - closed silently and added nothing. It closes itself on
         success instead, via onSaved. */
      closeOnConfirm={false}
      onConfirm={() => handleSubmit((d) => {
        if (offeredMissing) {
          setError('Choose at least one device this is offered for, or the “no device needed” option.');
          return;
        }
        setError(null);
        mutation.mutate(d);
      })()}
    >
      {/* ⚠ SCROLLS. AlertDialog sets no height, so a form this tall pushed Add
          below the fold on a laptop and the button could not be reached. Bounded
          here rather than in AlertDialog, which every other dialog shares. */}
      <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
        {/* ⛔ TYPE FIRST, THEN WHAT IT ANSWERS, THEN THE NAME. Ticking a
            device fills an empty name, so asking for the name before the
            ticks made you type what the next control was about to supply. */}
        <label className="block text-xs font-semibold text-darkGrey">Type<select {...register('ItemType')} className={inputCls}><option value="Accessory">Accessory</option><option value="Consumable">Consumable</option><option value="Device">Device</option></select></label>
        {/* ⛔ THE CATALOGUE, AS TICKS. Only for a Device, because only a device
            option can be asked for on a hardware request — an accessory line is
            matched a different way and a consumable is not requested at all. */}
        {mapping && (
          <fieldset className="rounded border border-border bg-lightBlue/20 px-2 py-2">
            <legend className="px-1 text-xs font-semibold text-darkGrey">Requests it can satisfy</legend>

            <p className="text-xs font-normal text-darkGrey/70">
              Tick what a requester can ask for that this line answers — several tiers can
              answer the same request. The first one ticked names the line below.
            </p>

            {catalogue.isLoading && <p className="mt-1 text-xs text-darkGrey/60">Loading the catalogue…</p>}
            {catalogue.isError && (
              <p role="alert" className="mt-1 text-xs text-red">
                Could not load the hardware catalogue. Add the item, then set this with “Satisfies…”.
              </p>
            )}

            {catalogue.data && deviceIds.length === 0 && (
              <p role="alert" className="mt-1 rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs font-normal text-darkGrey">
                Nothing ticked — this line will not be offered against any request.
              </p>
            )}

            <div className="mt-1 max-h-40 space-y-1 overflow-y-auto">
              {options.map((d) => (
                <label key={d.DeviceOptionId} className="flex items-center gap-2 text-sm font-normal">
                  <input
                    type="checkbox"
                    checked={deviceIds.includes(d.DeviceOptionId)}
                    onChange={() => toggle(d.DeviceOptionId, d.Name)}
                  />
                  {d.Name}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {/* ⛔ ONE PLACE TO ADD IT (Marty, 29/10/2026). The catalogue option a requester asks for and the
            stock line the warehouse counts are two records on purpose, but adding a real item should not
            mean doing it twice on two screens. Ticked, both are created - with this one name and price -
            and linked, in a single step that adds all of it or none. */}
        {isAdmin && (itemType === 'Device' || itemType === 'Accessory') && (
          <fieldset className="rounded border border-border bg-lightBlue/20 px-2 py-2">
            <legend className="px-1 text-xs font-semibold text-darkGrey">Hardware request catalogue</legend>
            <label className="flex items-start gap-2 text-xs font-semibold text-darkGrey">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={inCatalogue}
                onChange={(e) => setInCatalogue(e.target.checked)}
              />
              <span>
                Also let requesters ask for it
                <span className="mt-0.5 block font-normal text-darkGrey/70">
                  Adds it to the hardware catalogue with this name and price, and links this stock line to
                  it. Leave off for something only kept on the shelf.
                </span>
              </span>
            </label>

            {wantsCatalogue && itemType === 'Device' && (
              <p className="mt-2 text-xs font-normal text-darkGrey/80">
                A new catalogue device with this name is created and this line answers it. Set its other
                options (branch use, mobile number and so on) afterwards on the Hardware Catalogue page.
              </p>
            )}

            {wantsCatalogue && itemType === 'Accessory' && (
              <div className="mt-2">
                <p className="text-xs font-normal text-darkGrey/80">
                  Tick the devices it is offered for. Tick <strong>Required</strong> for a device that always
                  needs it &mdash; the power adapter for a Starlink, say. The &ldquo;no device needed&rdquo;
                  option lets it be asked for on its own.
                </p>
                {catalogue.isLoading && <p className="mt-1 text-xs text-darkGrey/60">Loading the catalogue…</p>}
                {catalogue.isError && (
                  <p role="alert" className="mt-1 text-xs text-red">Could not load the hardware catalogue, so nothing can be ticked.</p>
                )}
                {offeredMissing && catalogue.data && (
                  <p role="alert" className="mt-1 rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs font-normal text-darkGrey">
                    Nothing ticked &mdash; it would never be offered on a request.
                  </p>
                )}
                <div className="mt-1 max-h-40 space-y-1 overflow-y-auto">
                  {offerable.map((d) => (
                    <div key={d.DeviceOptionId} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm font-normal">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={offeredIds.includes(d.DeviceOptionId)}
                          onChange={() => toggleOffered(d.DeviceOptionId)}
                        />
                        {d.Name}{d.IsAccessoryOnly ? ' (no device needed)' : ''}
                      </label>
                      {offeredIds.includes(d.DeviceOptionId) && (
                        <label className="flex items-center gap-1 text-xs text-darkGrey/80">
                          <input
                            type="checkbox"
                            aria-label={`Required for ${d.Name}`}
                            checked={requiredIds.includes(d.DeviceOptionId)}
                            onChange={() => toggleRequired(d.DeviceOptionId)}
                          />
                          Required
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </fieldset>
        )}
        <label className="block text-xs font-semibold text-darkGrey">Name<input {...register('Name')} className={inputCls} />{formState.errors.Name && <span className="text-red">{formState.errors.Name.message}</span>}</label>
        <label className="block text-xs font-semibold text-darkGrey">Category
          <select {...register('Category')} className={inputCls}>
            <option value="">Select…</option>
            {STOCK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {formState.errors.Category && <span className="text-red">{formState.errors.Category.message}</span>}
        </label>
        {/* ⚠ ASKED AT CREATE TIME, because the reason a line is created is
            usually that we are about to buy some. Every field is optional —
            an unspecified line is still a line, just not one you can quote. */}
        <SpecFields register={register} errors={formState.errors} />
        {/* ⚠ Same place as on Edit: the description says what the thing IS,
            the parts itemise it. Empty is the normal case. */}
        <ComponentsEditor
          parts={parts}
          onChange={setParts}
          selfStockItemId={null}
          stockLines={stockLines.data ?? []}
          disabled={mutation.isPending}
        />
        {/* ⛔ WHERE IT IS HELD. Stock is quantity AT A PLACE — the same
            description at two warehouses is two lines, not one line of both. */}
        <label className="block text-xs font-semibold text-darkGrey">Warehouse
          <select
            {...register('LocationId', { setValueAs: (v) => (v === '' ? undefined : Number(v)) })}
            className={inputCls}
          >
            <option value="">Select…</option>
            {(warehouses.data ?? []).map((l) => (
              <option key={l.LocationId} value={l.LocationId}>{l.LocationDesc}</option>
            ))}
          </select>
          {formState.errors.LocationId && <span className="text-red">A warehouse is required</span>}
          {warehouses.isError && (
            <span className="block font-normal text-red">Could not load the warehouse list.</span>
          )}
        </label>
        <label className="block text-xs font-semibold text-darkGrey">Opening quantity<input type="number" {...register('InitialQty', { setValueAs: (v) => (v === '' ? 0 : Number(v)) })} className={inputCls} /></label>
        {/* ⛔ THE FLAG THAT CHANGES HOW HARDWARE REQUESTS BEHAVE. Off by
            default, and the hint says what turning it on actually does rather
            than restating its name. */}
        <label className="flex items-start gap-2 text-xs font-semibold text-darkGrey">
          <input type="checkbox" {...register('IsSerialised')} className="mt-0.5" />
          <span>
            Tracked by serial
            <span className="mt-0.5 block font-normal text-darkGrey/70">
              Each unit is an individual machine. A hardware request allocates these one at a
              time, and issuing one asks which machine went out. Leave off for cables, headsets
              and anything counted rather than identified.
            </span>
          </span>
        </label>

        {/* ⛔ NOT ASKED FOR A DEVICE (080). A stock line is ONE warehouse, so a
            threshold here would mean "this many at Sydney AND this many at
            Brisbane". For a device the number belongs to the catalogue option,
            where it covers every warehouse at once. An accessory satisfies no
            device, so this is the only mechanism it has. */}
        {itemType === 'Device' ? (
          <p className="rounded border border-border bg-bg px-2 py-1.5 text-xs font-normal text-darkGrey/80">
            <strong className="font-semibold text-darkGrey">Low stock</strong> for a device is set
            on the catalogue option as <em>Hold at least</em>, because it counts every warehouse
            together rather than this one line.
          </p>
        ) : (
          <label className="block text-xs font-semibold text-darkGrey">Low-stock threshold<input type="number" {...register('MinQty', { setValueAs: numberOrNull })} className={inputCls} /></label>
        )}
      </div>
    </AlertDialog>
  );
}


/**
 * ⚠ Which half failed. The item and its parts are two calls, so a failure
 * on the second leaves the first stored - and a flat "could not save" would
 * send somebody back to re-enter changes that are already in the database.
 */
class PartsSaveError extends Error {}

/**
 * Edit a stock line (099) — what it is, and what a supplier would be told.
 *
 * ⛔ THIS REPLACES THE RENAME DIALOG, DELIBERATELY. Rename could change one
 * field. PatchStockItem has accepted Make, Model, UnitCost and Notes since the
 * page was built and nothing has ever sent them, so four columns have been
 * unreachable from the screen for as long as the screen has existed. Adding a
 * second action beside Rename would have left two ways to change the name.
 *
 * ⚠ SENDS ONLY WHAT CHANGED, which is not tidiness. Category is a fixed list
 * now but was free text before, so a line imported with an off-list value shows
 * as “Select…” — and a body that always carried Category would wipe that value
 * on anyone who opened the dialog to fix a typo in the description.
 *
 * ⛔ CLEARING A NUMBER IS NOT POSSIBLE, AND THE HINT SAYS SO. PatchStockItem
 * reads MinQty and UnitCost as nullable ints, so null means “not sent” and
 * there is no way to express “set it back to nothing”. Rather than let a blank
 * box look like a save that did nothing, the number is simply not sent when it
 * is blank and the label says the current value is kept. The strings do not
 * have this problem — '' clears them, the Make/Model contract.
 */
function EditItemDialog({ item, onClose, onSaved }: { item: StockItem; onClose: () => void; onSaved: () => void }) {
  const [error, setError] = useState<string | null>(null);

  // ⚠ ONE baseline, used as the form's defaults AND as what "changed" is
  //   measured against. Two copies would drift, and the off-list category case
  //   above is exactly where that drift would do damage.
  const [defaults] = useState<EditFormData>(() => ({
    Name: item.Name,
    Category: (STOCK_CATEGORIES as readonly string[]).includes(item.Category ?? '')
      ? (item.Category as EditFormData['Category'])
      : '',
    MinQty: item.MinQty,
    UnitCost: item.UnitCost,
    Make: item.Make ?? '',
    Model: item.Model ?? '',
    SupplierPartNumber: item.SupplierPartNumber ?? '',
    Description: item.Description ?? '',
    Notes: item.Notes ?? '',
  }));

  const { register, handleSubmit, formState } = useForm<EditFormData>({
    resolver: zodResolver(editSchema),
    defaultValues: defaults,
  });

  // ── what this line is made of (106) ───────────────────────────────────────
  const [parts, setParts] = useState<PartDraft[] | null>(null);

  const components = useQuery({
    queryKey: ['stock-item-components', item.StockItemId],
    queryFn: ({ signal }) => getStockItemComponents(item.StockItemId, signal),
  });

  // ⚠ The saved list seeds the editor ONCE. Re-seeding on every render
  //   would throw away what the person is typing the moment the query
  //   refetched underneath them.
  const saved = components.data?.Components ?? [];
  const drafts = parts ?? (components.data ? toDrafts(saved) : []);

  // ⚠ Only the lines that can be POINTED AT. Reuses the list query's cache
  //   rather than fetching again - the page has it already.
  const stockLines = useQuery({
    queryKey: ['stock-items', { itemType: '', lowStockOnly: false }],
    queryFn: ({ signal }) => listStockItems({}, signal),
  });

  const mutation = useMutation({
    mutationFn: async (d: EditFormData) => {
      const body: StockItemWrite = {};
      const text = (v: string | null | undefined) => (v ?? '').trim();

      if (text(d.Name) !== text(defaults.Name)) body.Name = text(d.Name);
      if (text(d.Category) !== text(defaults.Category)) body.Category = text(d.Category);
      if (text(d.Make) !== text(defaults.Make)) body.Make = text(d.Make);
      if (text(d.Model) !== text(defaults.Model)) body.Model = text(d.Model);
      if (text(d.SupplierPartNumber) !== text(defaults.SupplierPartNumber)) {
        body.SupplierPartNumber = text(d.SupplierPartNumber);
      }
      if (text(d.Description) !== text(defaults.Description)) body.Description = text(d.Description);
      if (text(d.Notes) !== text(defaults.Notes)) body.Notes = text(d.Notes);

      // ⚠ `!= null` on purpose — a blank box is not sent at all, because the
      //   server would ignore it and the person would be looking at a save
      //   that silently did nothing.
      if (d.MinQty != null && d.MinQty !== defaults.MinQty) body.MinQty = d.MinQty;
      if (d.UnitCost != null && d.UnitCost !== defaults.UnitCost) body.UnitCost = d.UnitCost;

      // ⚠ The item FIRST, then the parts. Two calls, because the parts are
      //   a child collection with replace semantics and PatchStockItem sets
      //   only the columns it is sent.
      if (Object.keys(body).length > 0) await updateStockItem(item.StockItemId, body);

      // ⚠ Untouched list is not rewritten. A PUT soft deletes every row and
      //   inserts the list again, so saving a kit nobody edited would churn
      //   the table and fill the audit log with identical entries.
      if (components.data && !sameAsSaved(drafts, saved)) {
        try {
          await putStockItemComponents(item.StockItemId, toWrite(drafts));
        } catch (err) {
          // ⛔ SAY WHICH HALF LANDED. The item is already saved at this
          //   point; reporting a flat failure would send somebody back to
          //   re-enter changes that are in fact stored.
          throw new PartsSaveError(err instanceof ApiError ? err.message : 'The parts list could not be saved.');
        }
      }
    },
    onSuccess: onSaved,
    onError: (err) => setError(
      err instanceof PartsSaveError
        ? `The item was saved, but the parts list was not: ${err.message}`
        : err instanceof ApiError ? err.message : 'Could not save the stock line.',
    ),
  });

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Edit — ${item.Name}`}
      confirmLabel="Save"
      /* ⚠ The parts rows are a three-column layout; at the default 460px the
         Part box is a few characters wide and the text scrolls inside it. */
      wide
      /* ⛔ The default closes on confirm, which throws the error away — the same
         trap AddItemDialog documents. It closes itself via onSaved instead. */
      closeOnConfirm={false}
      onConfirm={() => handleSubmit((d) => { setError(null); mutation.mutate(d); })()}
    >
      <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}

        <label className="block text-xs font-semibold text-darkGrey">Name
          <input {...register('Name')} className={inputCls} />
          {formState.errors.Name && <span className="block font-normal text-red">{formState.errors.Name.message}</span>}
        </label>

        <label className="block text-xs font-semibold text-darkGrey">Category
          <select {...register('Category')} className={inputCls}>
            <option value="">Select…</option>
            {STOCK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <SpecFields register={register} errors={formState.errors} />

        {/* ⛔ UNDER THE DESCRIPTION, which is where Anthony asked for it and
            also where it reads correctly: the description says what the thing
            IS, the parts itemise it. A quote request renders the same way. */}
        {components.isLoading && (
          <p className="text-xs font-normal text-darkGrey/60">Loading the parts list…</p>
        )}
        {components.isError && (
          <p role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">
            The parts list could not be loaded, so it is not shown and saving will not change it.{' '}
            <button type="button" onClick={() => void components.refetch()} className="font-semibold underline">
              Try again
            </button>
          </p>
        )}
        {components.data && (
          <ComponentsEditor
            parts={drafts}
            onChange={setParts}
            selfStockItemId={item.StockItemId}
            stockLines={stockLines.data ?? []}
            disabled={mutation.isPending}
          />
        )}

        {/* ⛔ NOT ASKED FOR A DEVICE (080) — the threshold for one lives on the
            catalogue option, where it counts every warehouse together. Worded
            the same way as the Add dialog on purpose. */}
        {item.ItemType === 'Device' ? (
          <p className="rounded border border-border bg-bg px-2 py-1.5 text-xs font-normal text-darkGrey/80">
            <strong className="font-semibold text-darkGrey">Low stock</strong> for a device is set
            on the catalogue option as <em>Hold at least</em>, because it counts every warehouse
            together rather than this one line.
          </p>
        ) : (
          <label className="block text-xs font-semibold text-darkGrey">Low-stock threshold
            <input type="number" {...register('MinQty', { setValueAs: numberOrNull })} className={inputCls} />
            <span className="mt-0.5 block font-normal text-darkGrey/70">
              Leave blank to keep the current value.
            </span>
          </label>
        )}

        {/* ⚠ What this dialog does NOT touch, said rather than left to be
            discovered. Each one is a deliberate omission — see editSchema. */}
        <p className="text-xs text-darkGrey/70">
          The warehouse, the quantity on hand, whether it is tracked by serial, and what this
          line satisfies are all unchanged. Quantity moves through <em>Movement…</em>.
        </p>
      </div>
    </AlertDialog>
  );
}

/**
 * Retire a stock line (06/10/2026).
 *
 * ⛔ SOFT, like everything else — IsDeleted = 1, never a DELETE.
 *
 * ⛔ AND REFUSED WHILE THE CATALOGUE POINTS AT IT. Anthony: "option to retire
 * (if not linked to hardware catalogue)". A retired line still mapped to a
 * request device would be offered when somebody fulfils a request and then not
 * be there. The server refuses it; this says so before the click rather than
 * after, and the server remains the one that decides.
 */
function RetireDialog({ item, onClose, onSaved }: { item: StockItem; onClose: () => void; onSaved: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const linked = item.Devices.length;

  const mutation = useMutation({
    mutationFn: () => deleteStockItem(item.StockItemId),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not retire the stock line.'),
  });

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Retire — ${item.Name}`}
      confirmLabel="Retire"
      confirmDisabled={linked > 0}
      closeOnConfirm={false}
      onConfirm={() => { setError(null); mutation.mutate(); }}
    >
      <div className="space-y-2">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
        {linked > 0 ? (
          <p role="alert" className="rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
            This line still satisfies {item.Devices.map((d) => d.DeviceName).join(', ')}. Clear that
            under &ldquo;Satisfies&hellip;&rdquo; first, or a hardware request would be offered stock
            that is no longer there.
          </p>
        ) : (
          <p className="text-xs text-darkGrey/70">
            It stops appearing in stock and cannot be allocated. Its movement history is kept
            {item.QtyOnHand !== 0 && <>, and the {item.QtyOnHand} still recorded on hand stays on the ledger</>}.
          </p>
        )}
      </div>
    </AlertDialog>
  );
}

function TransactionDialog({ item, onClose, onSaved }: { item: StockItem; onClose: () => void; onSaved: () => void }) {
  const types: StockTxnType[] = item.ItemType === 'Consumable'
    ? ['Receipt', 'Consume', 'Adjust']
    : ['Receipt', 'CheckOut', 'CheckIn', 'Adjust'];
  const [txnType, setTxnType] = useState<StockTxnType>(types[0]);
  const [qty, setQty] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () => recordStockTransaction(item.StockItemId, { TxnType: txnType, Qty: Number(qty) }),
    onSuccess: onSaved,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not record the movement.'),
  });

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Stock movement — ${item.Name}`}
      confirmLabel="Record"
      closeOnConfirm={false}
      onConfirm={() => { setError(null); mutation.mutate(); }}
    >
      <div className="space-y-2">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
        <p className="text-xs text-darkGrey/70">On hand: {item.QtyOnHand}</p>
        <label className="block text-xs font-semibold text-darkGrey">Movement
          <select value={txnType} onChange={(e) => setTxnType(e.target.value as StockTxnType)} className={inputCls}>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-darkGrey">Quantity ({txnType === 'Adjust' ? 'sighted' : 'positive'})
          <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} />
        </label>
        {/* ⛔ SAYS WHAT WILL HAPPEN. An adjustment is now the count you SIGHTED,
            not an amount to add — the opposite reading of the same box, and the
            one that silently doubled a line under the old rule. Spelling out
            the movement is what makes the difference visible. */}
        {txnType === 'Adjust' && qty.trim() !== '' && Number.isFinite(Number(qty)) && (
          <p className="text-xs text-darkGrey/70">
            {Number(qty) < 0
              ? 'A sighted count cannot be negative.'
              : Number(qty) === item.QtyOnHand
                ? `Confirms ${item.QtyOnHand} on hand — no change.`
                : `On hand becomes ${Number(qty)} (${Number(qty) - item.QtyOnHand > 0 ? '+' : ''}${Number(qty) - item.QtyOnHand}).`}
          </p>
        )}
      </div>
    </AlertDialog>
  );
}

/**
 * Which hardware-request devices a stock line can satisfy (076).
 *
 * ⛔ WHY THIS EXISTS. The warehouse thinks in spec tiers — "High spec laptop",
 * "Standard", "OLT" — and a requester asks for "Laptop". Neither list is a
 * subset of the other, so they are two lists with a mapping, and this is where
 * the mapping is made.
 *
 * ⚠ A line that satisfies NOTHING is offered for nothing when somebody comes to
 * fulfil a request. Said out loud here, because an empty list looks exactly like
 * a line nobody has got to yet.
 */
function DevicesDialog({ item, onClose }: { item: StockItem; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<number[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['stock-item-devices', item.StockItemId],
    queryFn: ({ signal }) => getStockItemDevices(item.StockItemId, signal),
  });

  // Seeded from the server the first time it arrives, then owned locally so
  // ticking does not fight the query cache.
  const selected = chosen ?? query.data?.DeviceOptionIds ?? [];

  const mutation = useMutation({
    mutationFn: () => putStockItemDevices(item.StockItemId, selected),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-item-devices', item.StockItemId] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save.'),
  });

  const toggle = (id: number) =>
    setChosen(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`${item.Name} — requests it can satisfy`}
      confirmLabel="Save"
      closeOnConfirm={false}
      onConfirm={() => { setError(null); mutation.mutate(); }}
    >
      <div className="space-y-2">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
        {query.isLoading && <p className="text-xs text-darkGrey/60">Loading…</p>}
        {query.isError && <p role="alert" className="text-xs text-red">Could not load the device list.</p>}

        {query.data && (
          <>
            <p className="text-xs text-darkGrey/70">
              Tick the devices a requester can ask for that this line can answer.
            </p>

            {selected.length === 0 && (
              <p role="alert" className="rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
                Nothing ticked — this line will not be offered against any request.
              </p>
            )}

            <div className="max-h-64 space-y-1 overflow-y-auto">
              {query.data.Options.map((o) => (
                <label key={o.DeviceOptionId} className="flex items-center gap-2 text-sm font-normal">
                  <input
                    type="checkbox"
                    checked={selected.includes(o.DeviceOptionId)}
                    onChange={() => toggle(o.DeviceOptionId)}
                  />
                  {o.Name}
                  {!o.IsActive && <span className="text-xs text-darkGrey/60">(retired)</span>}
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    </AlertDialog>
  );
}

/**
 * Which hardware-request ACCESSORIES a stock line can satisfy (oscar migration 124).
 *
 * ⛔ THE SAME IDEA AS THE DEVICES DIALOG, for the other half of a request. Anthony,
 * 15/10/2026: "worked - now do accessories stock link." An accessory on a request
 * ("iPhone Case - Standard") is met from a stock line the warehouse names its own
 * way, so the two are two lists with a mapping, and this is where it is made.
 *
 * ⚠ A line that satisfies NOTHING is offered against no request. Said out loud, as
 * the devices dialog does, because an empty list looks like a line nobody has got to.
 */
function AccessoriesDialog({ item, onClose }: { item: StockItem; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [chosen, setChosen] = useState<number[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['stock-item-accessories', item.StockItemId],
    queryFn: ({ signal }) => getStockItemAccessories(item.StockItemId, signal),
  });

  const selected = chosen ?? query.data?.AccessoryOptionIds ?? [];

  const mutation = useMutation({
    mutationFn: () => putStockItemAccessories(item.StockItemId, selected),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-item-accessories', item.StockItemId] });
      queryClient.invalidateQueries({ queryKey: ['stock-accessory-links'] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save.'),
  });

  const toggle = (id: number) =>
    setChosen(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <AlertDialog
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`${item.Name} — accessories it can satisfy`}
      confirmLabel="Save"
      closeOnConfirm={false}
      onConfirm={() => { setError(null); mutation.mutate(); }}
    >
      <div className="space-y-2">
        {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">{error}</div>}
        {query.isLoading && <p className="text-xs text-darkGrey/60">Loading…</p>}
        {query.isError && <p role="alert" className="text-xs text-red">Could not load the accessory list.</p>}

        {query.data && (
          <>
            <p className="text-xs text-darkGrey/70">
              Tick the accessories a requester can ask for that this line can answer.
            </p>

            {selected.length === 0 && (
              <p role="alert" className="rounded border border-orange/40 bg-orange/10 px-2 py-1 text-xs text-darkGrey">
                Nothing ticked — this line will not be offered against any accessory.
              </p>
            )}

            <div className="max-h-64 space-y-1 overflow-y-auto">
              {query.data.Options.map((o) => (
                <label key={o.AccessoryOptionId} className="flex items-center gap-2 text-sm font-normal">
                  <input
                    type="checkbox"
                    checked={selected.includes(o.AccessoryOptionId)}
                    onChange={() => toggle(o.AccessoryOptionId)}
                  />
                  {o.Name}
                  {!o.IsActive && <span className="text-xs text-darkGrey/60">(retired)</span>}
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    </AlertDialog>
  );
}
